import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";
// import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";
import AddPreset from "./pages/admin/AddPreset";
import AddHotelInformation from "./pages/admin/AddHotelInformation";
import AdminLayout from "./components/AdminLayout";
import {
  ProtectedRoute,
  EmpProtectedRoute,
  KitchenProtectedRoute,
  PosProtectedRoute,
} from "./components/ProtectedRoute";
import Login from "./pages/admin/Login";
import Signup from "./pages/admin/Signup";
import PlansManagement from "./pages/admin/PlansManagement";
import PlanTransactions from "./pages/admin/PlanTransactions";
import ChoosePlan from "./pages/admin/ChoosePlan";
import PendingApproval from "./pages/admin/PendingApproval";
import BillingSuccess from "./pages/admin/BillingSuccess";
import MenuManagement from "./pages/admin/MenuManagement";
import Orders from "./pages/admin/Orders";
import LoyaltyPoints from "./pages/admin/LoyaltyPoints";
import POS from "./pages/pos/POS";
import Confirmation from "./pages/pos/Confirmation";
import TokenConfirmation from "./pages/pos/TokenConfirmation";
import AdminDashboard from "./pages/admin/AdminDashboard";
import Emplogin from "./Salespos/Emplogin";
import EmpRegister from "./pages/admin/EmpRegister";
import EmpPos from "./Salespos/EmpPos";
import EmpAllOrders from "./Salespos/EmpAllOrders";
import EmpManage from "./pages/admin/EmpManage";
import LocationAdminManagement from "./pages/admin/LocationAdminManagement";
import Discount from "./pages/admin/Discount";
import Choice from "./pages/pos/Choice";
import PayChoice from "./pages/pos/PayChoice";
import { OrderProvider } from "./pages/pos/OrderContext";
import CategoryManagement from "./pages/admin/CategoryManagement";
import ManageRoles from "./pages/admin/ManageRoles";
import Kitchenlogin from "./pages/kitchen/KitchenLogin";
import KitchenOrders from "./pages/kitchen/Kitchen";
import Notification from "./pages/kitchen/Notification";
import Setting from "./pages/kitchen/Setting";
import Support from "./pages/kitchen/Support";
import Poslogin from "./pages/pos/Poslogin";
import Landing from "./pages/Landing";
import Storefront from "./pages/Storefront";
import TaxManagement from "./pages/admin/TaxManage";
import { Toaster } from "react-hot-toast";
import Deals from "./pages/admin/Deals";
import PlatformLogo from "./pages/admin/PlatformLogo";
import PromotionalEmail from "./pages/admin/PromotionalEmail";
import RewardCampaigns from "./pages/admin/RewardCampaigns";
import DeveloperIntegrations from "./pages/admin/DeveloperIntegrations";
import { KitchenAuthProvider } from "./context/KitchenAuthContext";
import usePrintWebSocket from "./hooks/usePrintWebSocket";
import { useAuth } from "./context/AuthContext";
import SalesReport from "./pages/admin/reports/SalesReport";
import PaymentsReport from "./pages/admin/reports/PaymentsReport";
import ItemsReport from "./pages/admin/reports/ItemsReport";
import TaxReport from "./pages/admin/reports/TaxReport";
import Refunds from "./pages/admin/Refunds";
import Ledger from "./pages/admin/Ledger";
import Theme from "./pages/admin/Theme";

// Lives inside the Router so it can react to navigation (login, switching
// between admin/POS screens) and load that restaurant's theme settings.
const PlatformSettingsSync: React.FC = () => {
  const { pathname } = useLocation();
  const { syncPlatformSettings } = useAuth();
  useEffect(() => {
    syncPlatformSettings();
  }, [pathname, syncPlatformSettings]);
  return null;
};

const App: React.FC = () => {
  const { currencySymbol } = useAuth();
  usePrintWebSocket(currencySymbol);

  return (
    <OrderProvider>
      <KitchenAuthProvider>
        <Router>
          {/* <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          /> */}
          <Toaster position="top-center" reverseOrder={false} />
          <PlatformSettingsSync />

          <Routes>
            {/* Pos Routes */}

            {/* Home: links to every login (Admin, POS, Sales, Kitchen) */}
            <Route path="/" element={<Landing />} />
            <Route path="/pos/login" element={<Poslogin />} />
            {/* Public ordering link for a restaurant: eduw.in/<slug> */}
            <Route path="/:slug" element={<Storefront />} />
            <Route
              element={
                <PosProtectedRoute>
                  <Outlet />
                </PosProtectedRoute>
              }
            >
              <Route path="choice" element={<Choice />} />
              <Route path="pos" element={<POS />} />
              <Route
                path="pos/confirmation/:orderId"
                element={<Confirmation />}
              />
              <Route
                path="pos/token-confirmation/:tempOrderId"
                element={<TokenConfirmation />}
              />
              <Route path="pay" element={<PayChoice />} />
            </Route>

            {/* Admin Routes */}
            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin/signup" element={<Signup />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <Outlet />
                  </AdminLayout>
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="menu" element={<MenuManagement />} />
              <Route path="deals" element={<Deals />} />
              <Route path="orders" element={<Orders />} />
              <Route path="loyalty" element={<LoyaltyPoints />} />
              <Route path="category" element={<CategoryManagement />} />
              <Route path="managetax" element={<TaxManagement />} />
              <Route path="register" element={<EmpRegister />} />
              <Route path="roles" element={<ManageRoles />} />
              <Route path="manage" element={<EmpManage />} />
              <Route path="location-admins" element={<LocationAdminManagement />} />
              <Route path="plans" element={<PlansManagement />} />
              <Route path="plan-transactions" element={<PlanTransactions />} />
              <Route path="choose-plan" element={<ChoosePlan />} />
              <Route path="billing/success" element={<BillingSuccess />} />
              <Route path="pending-approval" element={<PendingApproval />} />
              <Route path="discount" element={<Discount />} />
              <Route path="addpreset" element={<AddPreset />} />
              <Route
                path="addhotelinformation"
                element={<AddHotelInformation />}
              />
              <Route path="platform-logo" element={<PlatformLogo />} />
              <Route
                path="developer-integrations"
                element={<DeveloperIntegrations />}
              />
              <Route path="promotional-email" element={<PromotionalEmail />} />
              <Route path="reward-campaigns" element={<RewardCampaigns />} />
              <Route path="refunds" element={<Refunds />} />
              <Route path="ledger" element={<Ledger />} />
              <Route path="theme" element={<Theme />} />
              <Route path="reports">
                <Route index element={<Navigate to="sales" replace />} />
                <Route path="sales" element={<SalesReport />} />
                <Route path="payments" element={<PaymentsReport />} />
                <Route path="items" element={<ItemsReport />} />
                <Route path="taxes" element={<TaxReport />} />
              </Route>
            </Route>

            {/* Salespos Routes */}
            <Route path="/login" element={<Emplogin />} />
            <Route
              path="/Sales"
              element={
                <EmpProtectedRoute>
                  <Outlet />
                </EmpProtectedRoute>
              }
            >
              <Route index element={<EmpPos />} />
              <Route path="all-orders" element={<EmpAllOrders />} />
            </Route>

            {/* Kitchen login */}
            <Route path="/kitchen/login" element={<Kitchenlogin />} />
            <Route
              path="/kitchen"
              element={
                <KitchenProtectedRoute>
                  <Outlet />
                </KitchenProtectedRoute>
              }
            >
              <Route index element={<KitchenOrders />} />
              <Route path="notification" element={<Notification />} />
              <Route path="setting" element={<Setting />} />
              <Route path="support" element={<Support />} />
            </Route>
          </Routes>
        </Router>
      </KitchenAuthProvider>
    </OrderProvider>
  );
};

export default App;
